/**
 * HomeApp — Root component for the Home single-page experience.
 *
 * Holds UI state and composes three panels:
 *   LeftPanel  — document list with filters
 *   CenterPanel — document preview / workspace
 *   RightPanel  — adaptive review (single doc) or batch workspace
 *
 * Data fetching, polling, and API actions are delegated to useHomeData().
 */
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { UserProfile } from "@clerk/clerk-react";
import { useClerk, useUser } from "@clerk/react-router";
import { Trash01, XClose } from "@untitledui/icons";
import { useNavigate } from "react-router-dom";
import { Dialog, Modal, ModalOverlay } from "@/components/application/modals/modal";
import { Button } from "@/components/base/buttons/button";
import { FeaturedIcon } from "@/components/foundations/featured-icon/featured-icons";
import { useAdminPreviewContext } from "@/contexts/admin-preview-context";
import { useBridgeHealth } from "@/hooks/use-bridge-health";
import { useClientBridge } from "@/hooks/use-client-bridge";
import { useHomeData } from "@/hooks/use-home-data";
import { getErrorDisplayMessage } from "@/lib/error-message-parser";
import { ApiException } from "@/services/api";
import { useDocumentsApi } from "@/services/api/documents-api";
import { useGlCodesApi } from "@/services/api/gl-codes-api";
import { usePlanEntitlements } from "@/services/api/plans-api";
import { useRoutingCodesApi } from "@/services/api/routing-codes-api";
import { ExceptionCodeEnum } from "@/types/api/common";
import { type DocumentDetailDto, DocumentStatusEnum } from "@/types/api/documents";
import type { GlCodeDto } from "@/types/api/gl-codes";
import type { RoutingCodeDto } from "@/types/api/routing-codes";
import type { HomeDocument } from "@/types/home";
import { exportDocumentsByType, exportDocumentsToCsv } from "@/utils/csv-export";
import { CenterPanel } from "./center-panel";
import { DEFAULT_EXPORT_FORMAT, DEFAULT_SORT, EXPIRED_STATUS_FILTER } from "./constants";
import { isDocumentSelectable, isRetryableExportFailure } from "./helpers";
import { LeftPanel } from "./left-panel";
import { RightPanel } from "./right-panel";
import type { RightPanelReviewHandle } from "./right-panel-review";
import { UploadModal } from "./upload-modal";

export default function HomeApp() {
    // ---------------------------------------------------------------------------
    // Data from API
    // ---------------------------------------------------------------------------
    const {
        sourceFiles,
        isLoading,
        typeOptions,
        orgOptions,
        orgData,
        deleteDocument,
        deleteDocuments,
        deleteFile,
        uploadFiles,
        documentDetail,
        isDetailLoading,
        fetchDocumentDetail,
        refresh,
        silentRefresh,
        startPolling,
        customFieldDefinitions,
        lineItemFieldDefinitions,
        updateCustomFields,
        updateDocumentOrganization,
        trackExportedDocIds,
        hasNoOrganization,
        isSessionExpired,
    } = useHomeData();

    const documentsApi = useDocumentsApi();
    const { hasBridge, bridgeName, canDirectSync } = useClientBridge();
    const { status: bridgeStatus, lastTimestamp: bridgeLastTimestamp, isRefreshing: bridgeIsRefreshing, refresh: refreshBridge } = useBridgeHealth(hasBridge);
    const isBridgeDead = hasBridge && bridgeStatus === "dead";
    const navigate = useNavigate();
    const { signOut } = useClerk();
    const { user } = useUser();
    const userName = user ? `${user.firstName || ""} ${user.lastName || ""}`.trim() || "User" : "User";
    const userInitials = user ? `${user.firstName?.[0] || ""}${user.lastName?.[0] || ""}`.toUpperCase() || "U" : "U";
    const [isLoggingOut, setIsLoggingOut] = useState(false);

    // ---------------------------------------------------------------------------
    // UI state
    // ---------------------------------------------------------------------------
    const [activeItemId, setActiveItemId] = useState<number | null>(null);
    const [isSelectionMode, setIsSelectionMode] = useState(false);
    const [selectedIds, setSelectedIds] = useState<Set<number>>(new Set());
    const [searchQuery, setSearchQuery] = useState("");
    const [statusFilter, setStatusFilter] = useState<Set<string>>(new Set());
    const [typeFilter, setTypeFilter] = useState<Set<string>>(new Set());
    const [orgFilter, setOrgFilter] = useState<Set<string>>(new Set());
    const [sortBy, setSortBy] = useState<string>(DEFAULT_SORT);
    const [exportFormat, setExportFormat] = useState<string>(DEFAULT_EXPORT_FORMAT);
    const [isUploadModalOpen, setIsUploadModalOpen] = useState(false);
    const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
    const [pendingDocumentId, setPendingDocumentId] = useState<number | null>(null);
    const [showUnsavedDialog, setShowUnsavedDialog] = useState(false);
    const [showDeleteDialog, setShowDeleteDialog] = useState(false);
    const [showProfileModal, setShowProfileModal] = useState(false);
    const [deleteTarget, setDeleteTarget] = useState<{ ids: number[]; label: string; isFile?: boolean; fileId?: number; fileIds?: number[] } | null>(null);
    const [isDeleting, setIsDeleting] = useState(false);
    const [isBridgeBannerDismissed, setIsBridgeBannerDismissed] = useState(false);
    const [exportBlockedNotice, setExportBlockedNotice] = useState<string | null>(null);
    const [showNoOrgDialog, setShowNoOrgDialog] = useState(false);
    const [showSessionExpiredDialog, setShowSessionExpiredDialog] = useState(false);
    const reviewPanelRef = useRef<RightPanelReviewHandle>(null);
    const [routingCodes, setRoutingCodes] = useState<RoutingCodeDto[]>([]);
    const hasFetchedRoutingCodesRef = useRef(false);
    const [glCodes, setGlCodes] = useState<GlCodeDto[]>([]);
    const hasFetchedGlCodesRef = useRef(false);

    // Client id for entitlements (routing codes) — from active doc or first document in list
    const clientId = useMemo(() => {
        const allDocs = sourceFiles.flatMap((sf) => sf.documents);
        const active = activeItemId !== null ? allDocs.find((d) => d.id === activeItemId) : null;
        return active?.clientId ?? allDocs[0]?.clientId ?? null;
    }, [sourceFiles, activeItemId]);

    const { hasRoutingCodesEntitlement, hasGlSuggestEntitlement } = usePlanEntitlements(clientId);
    const routingCodesApi = useRoutingCodesApi();
    const glCodesApi = useGlCodesApi();

    // Fetch routing codes once on page load when entitlement is available (not on every right-panel update)
    useEffect(() => {
        if (!hasRoutingCodesEntitlement) {
            setRoutingCodes([]);
            hasFetchedRoutingCodesRef.current = false;
            return;
        }
        if (hasFetchedRoutingCodesRef.current) return;
        hasFetchedRoutingCodesRef.current = true;
        let cancelled = false;
        const load = async () => {
            try {
                const codes = await routingCodesApi.getRoutingCodes();
                if (!cancelled) setRoutingCodes(codes);
            } catch {
                if (!cancelled) setRoutingCodes([]);
            }
        };
        load();
        return () => {
            cancelled = true;
        };
    }, [hasRoutingCodesEntitlement, routingCodesApi]);

    // Fetch the GL chart of accounts once when the GL-suggest entitlement is available
    // (same shape as routing codes: client-global, page-load-scoped)
    useEffect(() => {
        if (!hasGlSuggestEntitlement) {
            setGlCodes([]);
            hasFetchedGlCodesRef.current = false;
            return;
        }
        if (hasFetchedGlCodesRef.current) return;
        hasFetchedGlCodesRef.current = true;
        let cancelled = false;
        const load = async () => {
            try {
                const codes = await glCodesApi.getGlCodes();
                if (!cancelled) setGlCodes(codes);
            } catch {
                if (!cancelled) setGlCodes([]);
            }
        };
        load();
        return () => {
            cancelled = true;
        };
    }, [hasGlSuggestEntitlement, glCodesApi]);

    // Default the export format from the client's export mode: Direct Sync clients with a
    // bridge default to "external"; CSV-mode clients (bridge is lookup-only) stay on "csv".
    useEffect(() => {
        setExportFormat(canDirectSync ? "external" : "csv");
    }, [canDirectSync]);

    // Reset bridge banner dismiss when bridge recovers (so it shows again next outage)
    useEffect(() => {
        if (!isBridgeDead) {
            setIsBridgeBannerDismissed(false);
        }
    }, [isBridgeDead]);

    // Show no-org dialog automatically when detected
    useEffect(() => {
        if (hasNoOrganization) setShowNoOrgDialog(true);
    }, [hasNoOrganization]);

    // Show session-expired prompt when auth-expiry 401s persist past the silent token-refresh
    // retry. Deliberately NOT a sign-out — a transient 401 must not destroy a working session.
    useEffect(() => {
        if (isSessionExpired) setShowSessionExpiredDialog(true);
    }, [isSessionExpired]);

    // ---------------------------------------------------------------------------
    // Filtered & sorted data
    // ---------------------------------------------------------------------------
    const filteredSourceFiles = useMemo(() => {
        return (
            sourceFiles
                .map((sf) => ({
                    ...sf,
                    documents: sf.documents
                        .filter((doc) => {
                            // Hide non-inbound documents (system-generated only)
                            if (doc.isInbound === false) return false;
                            // Search
                            if (searchQuery) {
                                const q = searchQuery.toLowerCase();
                                if (!doc.vendorName.toLowerCase().includes(q) && !sf.fileName.toLowerCase().includes(q)) {
                                    return false;
                                }
                            }
                            // Status filter (empty set = show all) — error-aware, matches DocumentsPage logic
                            if (statusFilter.size > 0) {
                                // "Expired" is a synthetic filter: it matches the isRetentionExpired
                                // flag (retention window passed), not apiStatus. Union semantics —
                                // an expired doc passes regardless of other selected statuses.
                                const matchesExpired = statusFilter.has(EXPIRED_STATUS_FILTER) && !!doc.isRetentionExpired;

                                if (!matchesExpired) {
                                    const hasErrorCode = !!doc.errorCode;
                                    const isErrorFilter = statusFilter.has(DocumentStatusEnum.Error);

                                    if (hasErrorCode) {
                                        // Documents with errorCode are treated as errors regardless of apiStatus.
                                        // They only pass if the "Error" filter is active.
                                        if (!isErrorFilter) return false;
                                    } else {
                                        // Documents without errorCode match their apiStatus normally.
                                        if (!statusFilter.has(doc.apiStatus)) return false;
                                    }
                                }
                            }
                            // Type filter
                            if (typeFilter.size > 0 && !typeFilter.has(doc.documentType)) return false;
                            return true;
                        })
                        .sort((a, b) => {
                            const dateA = new Date(a.documentDate).getTime();
                            const dateB = new Date(b.documentDate).getTime();
                            return sortBy === "newest" ? dateB - dateA : dateA - dateB;
                        }),
                }))
                // Organization filter (at file level)
                .filter((sf) => {
                    if (orgFilter.size > 0) {
                        return sf.organizationId != null && orgFilter.has(String(sf.organizationId));
                    }
                    return true;
                })
                .filter((sf) => sf.documents.length > 0)
                // Sort file groups by their first document's date (already sorted within each group)
                .sort((a, b) => {
                    const dateA = new Date(a.documents[0].documentDate).getTime();
                    const dateB = new Date(b.documents[0].documentDate).getTime();
                    return sortBy === "newest" ? dateB - dateA : dateA - dateB;
                })
        );
    }, [sourceFiles, searchQuery, statusFilter, typeFilter, orgFilter, sortBy]);

    // Total visible document count
    const totalDocCount = useMemo(() => filteredSourceFiles.reduce((sum, sf) => sum + sf.documents.length, 0), [filteredSourceFiles]);

    // Count of selectable documents (all except mid-upload)
    const selectableDocCount = useMemo(
        () => filteredSourceFiles.reduce((sum, sf) => sum + sf.documents.filter(isDocumentSelectable).length, 0),
        [filteredSourceFiles],
    );

    // Active document for center + right panel
    const activeDocument = useMemo(() => {
        if (activeItemId === null) return null;
        for (const sf of sourceFiles) {
            const doc = sf.documents.find((d) => d.id === activeItemId);
            if (doc) return doc;
        }
        return null;
    }, [sourceFiles, activeItemId]);

    // Fetch document detail when active document changes
    useEffect(() => {
        if (activeItemId !== null) {
            fetchDocumentDetail(activeItemId);
        }
    }, [activeItemId, fetchDocumentDetail]);

    // Re-fetch document detail when active document finishes processing
    // This ensures field values are populated when transitioning from processing to ready
    useEffect(() => {
        if (activeDocument?.isProcessingEndStateReached && activeDocument.id > 0) {
            fetchDocumentDetail(activeDocument.id);
        }
    }, [activeDocument?.isProcessingEndStateReached, activeDocument?.id, fetchDocumentDetail]);

    // Vendor-name field code — only resolved when the active document's vendor lookup failed.
    // The failure-mode right panel shows only this field for correction. Null otherwise.
    const [vendorNameFieldCode, setVendorNameFieldCode] = useState<string | null>(null);
    useEffect(() => {
        const isFailure = activeDocument?.errorCode === ExceptionCodeEnum.BizVendorNotFound;
        const docTypeId = documentDetail?.documentType?.id;
        if (isFailure && docTypeId && documentDetail?.id === activeDocument?.id) {
            let cancelled = false;
            documentsApi
                .getVendorNameField(docTypeId, documentDetail.organizationId ?? null)
                .then((def) => {
                    if (!cancelled) setVendorNameFieldCode(def?.fieldCode ?? null);
                })
                .catch(() => {
                    if (!cancelled) setVendorNameFieldCode(null);
                });
            return () => {
                cancelled = true;
            };
        }
        setVendorNameFieldCode(null);
    }, [activeDocument?.id, activeDocument?.errorCode, documentDetail?.id, documentDetail?.documentType?.id, documentDetail?.organizationId, documentsApi]);

    // Selected documents for batch panel
    const selectedDocs: HomeDocument[] = useMemo(() => {
        const docs: HomeDocument[] = [];
        for (const sf of filteredSourceFiles) {
            for (const doc of sf.documents) {
                if (selectedIds.has(doc.id)) docs.push(doc);
            }
        }
        return docs;
    }, [filteredSourceFiles, selectedIds]);

    // ---------------------------------------------------------------------------
    // Handlers
    // ---------------------------------------------------------------------------
    const handleActivateDocument = useCallback(
        (docId: number) => {
            if (hasUnsavedChanges && activeItemId !== null && activeItemId !== docId) {
                setPendingDocumentId(docId);
                setShowUnsavedDialog(true);
                return;
            }
            setActiveItemId(docId);
        },
        [hasUnsavedChanges, activeItemId],
    );

    const handleSelectDuplicateFile = useCallback(
        (fileStorageId: number) => {
            const file = sourceFiles.find((sf) => sf.id === fileStorageId);
            if (file && file.documents.length > 0) {
                setActiveItemId(file.documents[0].id);
            }
        },
        [sourceFiles],
    );

    const handleToggleSelectMode = useCallback(() => {
        setIsSelectionMode((prev) => !prev);
        setSelectedIds(new Set());
    }, []);

    const handleToggleDocSelection = useCallback(
        (docId: number) => {
            setSelectedIds((prev) => {
                const next = new Set(prev);
                if (next.has(docId)) {
                    next.delete(docId);
                } else {
                    const doc = filteredSourceFiles.flatMap((sf) => sf.documents).find((d) => d.id === docId);
                    if (doc && isDocumentSelectable(doc)) {
                        next.add(docId);
                    }
                }
                return next;
            });
        },
        [filteredSourceFiles],
    );

    const handleSelectAll = useCallback(() => {
        setSelectedIds((prev) => {
            const selectableIds = filteredSourceFiles
                .flatMap((sf) => sf.documents)
                .filter(isDocumentSelectable)
                .map((d) => d.id);
            if (prev.size === selectableIds.length && selectableIds.every((id) => prev.has(id))) {
                return new Set();
            }
            return new Set(selectableIds);
        });
    }, [filteredSourceFiles]);

    const handleSortChange = useCallback((sort: string) => {
        setSortBy(sort);
    }, []);

    const handleApprove = useCallback(
        async (_docId: number) => {
            // Placeholder — real approval wired in Step 12
            // For now, just refresh data after a hypothetical status change
            await refresh();
        },
        [refresh],
    );

    const handleCsvExport = useCallback(
        async (doc: DocumentDetailDto) => {
            // Generate filename with timestamp and document type (same as DocumentsPage)
            const timestamp = new Date().toISOString().replace(/[:.]/g, "-").slice(0, -5);

            const documentTypeName = doc.documentType?.name || "document";
            const safeTypeName = documentTypeName
                .toLowerCase()
                .replace(/\s+/g, "-")
                .replace(/[^a-z0-9-]/g, "");

            const docIdentifier = doc.fileName?.replace(/\.[^.]+$/, "") || `doc-${doc.id}`;
            const safeDocIdentifier = docIdentifier
                .toLowerCase()
                .replace(/\s+/g, "-")
                .replace(/[^a-z0-9-]/g, "")
                .substring(0, 30);

            const filename = `${safeTypeName}-${safeDocIdentifier}-${timestamp}.csv`;

            // Mark Exported first — the server refuses (BizExportBlocked) when the document has
            // an open blocking exception, and a refused document must not produce a CSV download.
            await documentsApi.batchUpdateStatus({
                documentIds: [doc.id],
                status: DocumentStatusEnum.Exported,
            });

            // Trigger CSV download
            exportDocumentsToCsv([doc], filename);

            // Refresh file list to show updated status (silent to avoid full-page loading flash)
            await silentRefresh();
        },
        [documentsApi, silentRefresh],
    );

    const handleExternalExport = useCallback(
        async (docId: number) => {
            await documentsApi.exportDocuments([docId], "external");
            await silentRefresh();
            trackExportedDocIds([docId]);
        },
        [documentsApi, silentRefresh, trackExportedDocIds],
    );

    const handleRelookupVendor = useCallback(
        async (docId: number) => {
            await documentsApi.relookupVendor(docId);
            // Re-fetch the file list; the document flips back to a processing state.
            await silentRefresh();
            // The failed doc wasn't being polled (it had an errorCode), so the interval may be
            // stopped. Resume it so the panel auto-updates when the re-lookup completes — matching
            // the normal upload→processing→ready flow (no manual refresh needed).
            startPolling();
        },
        [documentsApi, silentRefresh, startPolling],
    );

    const handleRetryExport = useCallback(
        async (docId: number) => {
            await documentsApi.retryExport(docId);
            // The failed-export error clears (a fresh Queued job is now the latest attempt), so the
            // document returns to the in-flight "Syncing to ERP…" state. Export polling picks up
            // the outcome (Exported, or the error reappearing on another failure).
            await silentRefresh();
            trackExportedDocIds([docId]);
        },
        [documentsApi, silentRefresh, trackExportedDocIds],
    );

    const handleDelete = useCallback(
        (docId: number) => {
            const doc = sourceFiles.flatMap((sf) => sf.documents).find((d) => d.id === docId);
            const label = doc?.vendorName || `Document #${docId}`;

            // For synthetic documents (id=0), we delete the source file instead
            if (docId <= 0 && doc?.sourceFileId) {
                const sf = sourceFiles.find((s) => s.id === doc.sourceFileId);
                const fileLabel = sf?.fileName || label;
                setDeleteTarget({ ids: [docId], label: fileLabel, isFile: true, fileId: doc.sourceFileId });
            } else {
                setDeleteTarget({ ids: [docId], label });
            }
            setShowDeleteDialog(true);
        },
        [sourceFiles],
    );

    const handleBatchExport = useCallback(async () => {
        const docIds = Array.from(selectedIds);
        if (docIds.length === 0) return;

        try {
            if (exportFormat === "external" && canDirectSync) {
                // Direct Sync — push to ERP via backend (backend sets status to Queued).
                // Documents whose previous export failed retry their exact failed job instead
                // (preserves the workflow step link and audit history of the failed attempt).
                const retryIds = selectedDocs.filter(isRetryableExportFailure).map((d) => d.id);
                const exportIds = docIds.filter((id) => !retryIds.includes(id));
                if (exportIds.length > 0) {
                    await documentsApi.exportDocuments(exportIds, "external");
                }
                for (const retryId of retryIds) {
                    await documentsApi.retryExport(retryId);
                }
                trackExportedDocIds(docIds);
            } else if (exportFormat === "external" && !canDirectSync) {
                // Direct Sync unavailable (no bridge, or CSV-mode client) — do nothing
                return;
            } else {
                // CSV export — client-side file download
                const response = await documentsApi.getDocumentsByIds(docIds);
                const documentsToExport = response.documents;
                if (documentsToExport.length === 0) return;

                // Mark Exported first — the server refuses (BizExportBlocked) when any selected
                // document has an open blocking exception; no CSV must be produced in that case.
                await documentsApi.batchUpdateStatus({
                    documentIds: docIds,
                    status: DocumentStatusEnum.Exported,
                });

                const timestamp = new Date().toISOString().replace(/[:.]/g, "-").slice(0, -5);
                exportDocumentsByType(documentsToExport, timestamp);
            }

            // Refresh list silently + clear selection
            await silentRefresh();
            setSelectedIds(new Set());
            setIsSelectionMode(false);
        } catch (err) {
            console.error("Batch export failed:", err);
            // Server-side export gate (stale-tab case): some selected documents have unresolved
            // blocking exceptions. Show why (the server lists the ids) and refresh the list so
            // the error indicators reappear.
            if (err instanceof ApiException && err.code === ExceptionCodeEnum.BizExportBlocked) {
                setExportBlockedNotice(err.errorResponse?.message ?? getErrorDisplayMessage(err.code));
                await silentRefresh();
            }
        }
    }, [selectedIds, selectedDocs, documentsApi, silentRefresh, exportFormat, canDirectSync, trackExportedDocIds]);

    const handleBatchDelete = useCallback(() => {
        const allDocs = filteredSourceFiles.flatMap((sf) => sf.documents);
        const realDocIds: number[] = [];
        const fileIds: number[] = [];

        for (const id of selectedIds) {
            const doc = allDocs.find((d) => d.id === id);
            if (!doc) continue;
            if (id <= 0 && doc.sourceFileId) {
                fileIds.push(doc.sourceFileId);
            } else {
                realDocIds.push(id);
            }
        }

        const totalCount = realDocIds.length + fileIds.length;
        const label = `${totalCount} item${totalCount > 1 ? "s" : ""}`;
        setDeleteTarget({ ids: realDocIds, fileIds, label });
        setShowDeleteDialog(true);
    }, [selectedIds, filteredSourceFiles]);

    const handleConfirmDelete = useCallback(async () => {
        if (!deleteTarget) return;
        setIsDeleting(true);
        try {
            // Single synthetic document (processing file placeholder) — delete source file
            if (deleteTarget.isFile && deleteTarget.fileId) {
                await deleteFile(deleteTarget.fileId);
                setActiveItemId(null);
            } else {
                // Batch: delete real documents in one call, files one-by-one
                const { ids: realDocIds, fileIds = [] } = deleteTarget;

                if (realDocIds.length === 1 && fileIds.length === 0) {
                    // Single real document — use single-delete path
                    await deleteDocument(realDocIds[0]);
                    if (activeItemId === realDocIds[0]) setActiveItemId(null);
                } else if (realDocIds.length > 0 || fileIds.length > 0) {
                    // Batch — real docs in one API call, files sequentially
                    if (realDocIds.length > 0) await deleteDocuments(realDocIds);
                    for (const fileId of fileIds) await deleteFile(fileId);
                    if (activeItemId !== null && (realDocIds.includes(activeItemId) || fileIds.length > 0)) {
                        setActiveItemId(null);
                    }
                }

                if (isSelectionMode) {
                    setSelectedIds(new Set());
                    setIsSelectionMode(false);
                }
            }
        } catch (err) {
            console.error("Delete failed:", err);
        } finally {
            setIsDeleting(false);
            setShowDeleteDialog(false);
            setDeleteTarget(null);
        }
    }, [deleteTarget, deleteDocument, deleteDocuments, deleteFile, activeItemId, isSelectionMode]);

    const handleSaveAndSwitch = useCallback(async () => {
        try {
            await reviewPanelRef.current?.save();
        } catch {
            // save failed — stay on current document
            setShowUnsavedDialog(false);
            setPendingDocumentId(null);
            return;
        }
        setHasUnsavedChanges(false);
        setShowUnsavedDialog(false);
        if (pendingDocumentId !== null) {
            setActiveItemId(pendingDocumentId);
            setPendingDocumentId(null);
        }
    }, [pendingDocumentId]);

    const handleDiscardAndSwitch = useCallback(() => {
        setHasUnsavedChanges(false);
        setShowUnsavedDialog(false);
        if (pendingDocumentId !== null) {
            setActiveItemId(pendingDocumentId);
            setPendingDocumentId(null);
        }
    }, [pendingDocumentId]);

    const handleUploadClick = useCallback(() => {
        if (hasNoOrganization) {
            setShowNoOrgDialog(true);
            return;
        }
        setIsUploadModalOpen(true);
    }, [hasNoOrganization]);

    const handleProfileOpen = useCallback(() => {
        setShowProfileModal(true);
    }, []);

    const handleLogout = useCallback(async () => {
        if (isLoggingOut) return;
        setIsLoggingOut(true);
        try {
            await signOut();
            navigate("/login", { replace: true });
        } catch (error) {
            console.error("Logout failed:", error);
            setIsLoggingOut(false);
        }
    }, [isLoggingOut, signOut, navigate]);

    const { previewClientId } = useAdminPreviewContext();
    const handleBackToAdmin = previewClientId != null ? () => navigate(`/admin/clients/${previewClientId}`) : undefined;

    // ---------------------------------------------------------------------------
    // Render
    // ---------------------------------------------------------------------------
    if (isLoading) {
        return (
            <div className="flex h-screen items-center justify-center bg-primary">
                <p className="text-sm text-tertiary">Loading documents...</p>
            </div>
        );
    }

    return (
        <div className="flex h-screen flex-col overflow-hidden bg-primary font-sans antialiased">
            <div className="flex min-h-0 flex-1 overflow-hidden">
                <LeftPanel
                    userName={userName}
                    userInitials={userInitials}
                    sourceFiles={filteredSourceFiles}
                    activeItemId={activeItemId}
                    isSelectionMode={isSelectionMode}
                    selectedIds={selectedIds}
                    totalDocCount={totalDocCount}
                    selectableDocCount={selectableDocCount}
                    sortBy={sortBy}
                    searchQuery={searchQuery}
                    statusFilter={statusFilter}
                    typeFilter={typeFilter}
                    orgFilter={orgFilter}
                    typeOptions={typeOptions}
                    orgOptions={orgOptions}
                    onActivateDocument={handleActivateDocument}
                    onToggleSelectMode={handleToggleSelectMode}
                    onToggleDocSelection={handleToggleDocSelection}
                    onSelectAll={handleSelectAll}
                    onSearchChange={setSearchQuery}
                    onStatusFilterChange={setStatusFilter}
                    onTypeFilterChange={setTypeFilter}
                    onOrgFilterChange={setOrgFilter}
                    onSortChange={handleSortChange}
                    onUploadClick={handleUploadClick}
                    onProfile={handleProfileOpen}
                    onLogout={handleLogout}
                    onBackToAdmin={handleBackToAdmin}
                    hasBridge={hasBridge}
                    isBridgeDead={isBridgeDead}
                    hasNoOrgs={hasNoOrganization}
                    bridgeStatus={bridgeStatus}
                    bridgeLastTimestamp={bridgeLastTimestamp}
                    bridgeIsRefreshing={bridgeIsRefreshing}
                    onBridgeRefresh={refreshBridge}
                />

                <CenterPanel document={activeDocument} documentDetail={documentDetail} />

                <RightPanel
                    isSelectionMode={isSelectionMode}
                    activeDocument={activeDocument}
                    documentDetail={documentDetail}
                    isDetailLoading={isDetailLoading}
                    customFieldDefinitions={customFieldDefinitions}
                    lineItemFieldDefinitions={lineItemFieldDefinitions}
                    selectedDocs={selectedDocs}
                    exportFormat={exportFormat}
                    hasBridge={hasBridge}
                    canDirectSync={canDirectSync}
                    bridgeName={bridgeName}
                    routingCodes={routingCodes}
                    glCodes={glCodes}
                    glSuggestionsEnabled={hasGlSuggestEntitlement}
                    orgs={orgData}
                    isAdminPreview={previewClientId != null}
                    onApprove={handleApprove}
                    onDelete={handleDelete}
                    onUpdateFields={updateCustomFields}
                    onUpdateOrganization={updateDocumentOrganization}
                    onCsvExport={handleCsvExport}
                    onExternalExport={handleExternalExport}
                    onRetryExport={handleRetryExport}
                    onRelookup={handleRelookupVendor}
                    vendorNameFieldCode={vendorNameFieldCode}
                    onBatchExport={handleBatchExport}
                    onBatchDelete={handleBatchDelete}
                    onExportFormatChange={setExportFormat}
                    onHasChangesChange={setHasUnsavedChanges}
                    onSelectDuplicateFile={handleSelectDuplicateFile}
                    onReloadDetail={fetchDocumentDetail}
                    reviewRef={reviewPanelRef}
                />
            </div>

            <UploadModal isOpen={isUploadModalOpen} onClose={() => setIsUploadModalOpen(false)} onUpload={uploadFiles} orgs={orgData} />

            {showUnsavedDialog && (
                <ModalOverlay isOpen isDismissable={false}>
                    <Modal className="max-w-md">
                        <Dialog>
                            <div className="rounded-xl bg-primary p-6 shadow-xl">
                                <h2 className="mb-2 text-lg font-semibold text-primary">Unsaved Field Changes</h2>
                                <p className="mb-6 text-tertiary">
                                    You have unsaved field changes. Would you like to save your updates before switching documents?
                                </p>
                                <div className="flex justify-end gap-3">
                                    <Button color="primary-destructive" size="md" onClick={handleDiscardAndSwitch}>
                                        Discard
                                    </Button>
                                    <Button color="primary" size="md" onClick={handleSaveAndSwitch}>
                                        Save
                                    </Button>
                                </div>
                            </div>
                        </Dialog>
                    </Modal>
                </ModalOverlay>
            )}

            {/* Delete Confirmation Dialog */}
            {showDeleteDialog && deleteTarget && (
                <ModalOverlay isOpen isDismissable={false}>
                    <Modal className="max-w-md">
                        <Dialog>
                            <div className="rounded-xl bg-primary p-6 shadow-xl">
                                <div className="mb-4">
                                    <FeaturedIcon icon={Trash01} color="error" theme="light" size="md" />
                                </div>
                                <h2 className="mb-2 text-lg font-semibold text-primary">Delete Document?</h2>
                                <p className="mb-6 text-tertiary">
                                    Are you sure you want to delete &ldquo;{deleteTarget.label}&rdquo;? This action cannot be undone.
                                </p>
                                <div className="flex justify-end gap-3">
                                    <Button
                                        color="secondary"
                                        size="md"
                                        onClick={() => {
                                            setShowDeleteDialog(false);
                                            setDeleteTarget(null);
                                        }}
                                        isDisabled={isDeleting}
                                    >
                                        Cancel
                                    </Button>
                                    <Button color="primary-destructive" size="md" iconLeading={Trash01} onClick={handleConfirmDelete} isLoading={isDeleting}>
                                        Delete
                                    </Button>
                                </div>
                            </div>
                        </Dialog>
                    </Modal>
                </ModalOverlay>
            )}

            {/* No Organization Dialog */}
            {showNoOrgDialog && (
                <ModalOverlay isOpen isDismissable={false}>
                    <Modal className="max-w-md">
                        <Dialog>
                            <div className="rounded-xl bg-primary p-6 shadow-xl">
                                <h2 className="mb-2 text-lg font-semibold text-primary">No Organization Assigned</h2>
                                <p className="mb-6 text-tertiary">You are not assigned to any organization, please contact system administrator</p>
                                <div className="flex justify-end">
                                    <Button color="primary" size="md" onClick={() => setShowNoOrgDialog(false)}>
                                        OK
                                    </Button>
                                </div>
                            </div>
                        </Dialog>
                    </Modal>
                </ModalOverlay>
            )}

            {/* Session Expired Dialog */}
            {showSessionExpiredDialog && (
                <ModalOverlay isOpen isDismissable={false}>
                    <Modal className="max-w-md">
                        <Dialog>
                            <div className="rounded-xl bg-primary p-6 shadow-xl">
                                <h2 className="mb-2 text-lg font-semibold text-primary">Session Expired</h2>
                                <p className="mb-6 text-tertiary">Your session has expired. Refresh the page to sign in again — your documents are safe.</p>
                                <div className="flex justify-end gap-3">
                                    <Button color="secondary" size="md" onClick={() => setShowSessionExpiredDialog(false)}>
                                        Dismiss
                                    </Button>
                                    <Button color="primary" size="md" onClick={() => window.location.reload()}>
                                        Refresh Page
                                    </Button>
                                </div>
                            </div>
                        </Dialog>
                    </Modal>
                </ModalOverlay>
            )}

            {/* User Profile Modal */}
            {showProfileModal && (
                <ModalOverlay isOpen isDismissable onOpenChange={(open) => !open && setShowProfileModal(false)}>
                    <Modal className="max-w-[960px]">
                        <Dialog>
                            <div className="flex h-[min(90vh,800px)] w-full flex-col overflow-hidden rounded-xl border border-secondary bg-primary shadow-xl">
                                <div className="flex shrink-0 items-center justify-between border-b border-secondary px-6 py-4">
                                    <h2 className="text-lg font-semibold text-primary">Profile</h2>
                                    <Button color="tertiary" size="sm" iconLeading={XClose} onClick={() => setShowProfileModal(false)} aria-label="Close" />
                                </div>
                                <div className="custom-scrollbar min-h-0 flex-1 overflow-y-auto">
                                    <UserProfile
                                        routing="hash"
                                        appearance={{
                                            elements: {
                                                rootBox: "w-full h-full",
                                                cardBox: "w-full h-full shadow-none",
                                                card: "shadow-none w-full h-full",
                                                scrollBox: "overflow-hidden",
                                                navbar: "border-r border-secondary",
                                            },
                                        }}
                                    />
                                </div>
                            </div>
                        </Dialog>
                    </Modal>
                </ModalOverlay>
            )}

            {/* Export-blocked popup — server-side export gate refused the request (stale tab) */}
            {exportBlockedNotice && (
                <div className="fixed bottom-6 left-6 z-50 flex items-center gap-3 rounded-lg bg-error-solid px-4 py-3 shadow-lg">
                    <span className="text-sm font-medium text-white">{exportBlockedNotice}</span>
                    <button
                        type="button"
                        onClick={() => setExportBlockedNotice(null)}
                        className="ml-1 shrink-0 text-white/70 transition hover:text-white"
                        aria-label="Dismiss"
                    >
                        <XClose className="size-4" />
                    </button>
                </div>
            )}

            {/* Bridge-down popup — bottom-left toast (Google Drive style) */}
            {hasBridge && isBridgeDead && !isBridgeBannerDismissed && (
                <div className="fixed bottom-6 left-6 z-50 flex items-center gap-3 rounded-lg bg-brand-solid px-4 py-3 shadow-lg">
                    <span className="text-sm font-medium text-white">
                        Bridge connection lost — uploads and processing are temporarily unavailable. Please contact the system administrator.
                    </span>
                    <button
                        type="button"
                        onClick={() => setIsBridgeBannerDismissed(true)}
                        className="ml-1 shrink-0 text-white/70 transition hover:text-white"
                        aria-label="Dismiss"
                    >
                        <XClose className="size-4" />
                    </button>
                </div>
            )}
        </div>
    );
}
